import { useMemo } from 'react';
import {
  useGetDealerByIdQuery,
  useGetActiveDealersByManufacturerIdQuery,
} from './dealer-api-slice';
import { useAppSelector } from '../../store/hooks';
import { selectManageDealers } from '../user';

/**
 * Custom hook to fetch dealer data based on user role and manufacturer context
 *
 * This hook centralizes the logic for fetching dealer data,
 * handling the different data access patterns based on user roles.
 */
export const useDealerData = (options: {
  manufacturerId?: number;
  currentUserDealerId?: number;
}) => {
  const { manufacturerId, currentUserDealerId } = options;

  const canManageDealers = useAppSelector(selectManageDealers);

  // Determine which dealer ID to fetch for non-admin users
  const dealerIdToFetch = useMemo(() => {
    if (canManageDealers) {
      return undefined;
    } else {
      return currentUserDealerId;
    }
  }, [currentUserDealerId, canManageDealers]);

  // For users who can manage dealers, fetch all dealers for the selected manufacturer
  const { data: allDealers = [], isLoading: isLoadingAllDealers } =
    useGetActiveDealersByManufacturerIdQuery(manufacturerId ?? 0, {
      skip: !manufacturerId || !canManageDealers, // Skip if no manufacturer ID or user can't manage dealers
      refetchOnMountOrArgChange: true,
    });

  // For users who can't manage dealers, fetch only their dealer
  const { data: singleDealer, isLoading: isLoadingSingleDealer } = useGetDealerByIdQuery(
    dealerIdToFetch?.toString() ?? '',
    {
      skip: canManageDealers || !dealerIdToFetch, // Skip if user can manage dealers or no dealer ID
      refetchOnMountOrArgChange: true,
    }
  );

  // Combine the results
  const dealers = useMemo(() => {
    if (canManageDealers) {
      return allDealers;
    } else if (singleDealer) {
      return [singleDealer];
    }
    return [];
  }, [canManageDealers, allDealers, singleDealer]);

  const isLoading = canManageDealers ? isLoadingAllDealers : isLoadingSingleDealer;

  return {
    dealers,
    isLoading,
    dealerIdToFetch,
  };
};
